/**
 * Модалка «Новый вопрос» / «Изменить вопрос» вкладки «Вопросы опросника» (ecology-survey, макет
 * панели администратора 16.09 + ответы заказчика 17.09). Поля: текст, раздел, тип ответа, варианты
 * (по одному в строке), подсказка, уточнения при ответе «Да» (до трёх: дата, число, текст или выбор
 * варианта — у инвентаризации их два по анкете), «показывать, если» и включённость.
 *
 * У встроенного вопроса анкеты тип ответа, варианты, типы уточнений и условие показа не меняются —
 * на них опираются стартовые правила (сервер берёт их из текущего вопроса), поля заблокированы;
 * правятся текст, подсказка, раздел, подписи уточнений и включённость.
 */
import { useEffect, useId, useMemo, useState, type FormEvent } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import {
  adminApi,
  type EcoAnswerType,
  type EcoClarification,
  type EcoClarificationType,
  type EcoQuestion,
  type EcoQuestionInput,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import {
  ECO_ANSWER_TYPE_LABELS,
  ECO_CLARIFICATION_TYPE_LABELS,
  ECO_SURVEY_SECTIONS,
  ecoAnswerValues,
  ecoQuestionToInput,
} from '@/lib/ecology'
import { LabeledCheckbox } from '@/sout/labeled-checkbox'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'

const TEXTAREA_CLASS =
  'w-full rounded-8 border border-line-strong bg-white px-3 py-2 text-14 text-txt outline-none transition-colors placeholder:text-txt-tertiary hover:border-line-strong focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:bg-subtle disabled:opacity-50'

/** Значение «показывать всегда»: Radix Select не допускает пустое значение пункта. */
const ALWAYS = 'always'
const MAX_CLARIFICATIONS = 3

const ANSWER_TYPES = Object.keys(ECO_ANSWER_TYPE_LABELS) as EcoAnswerType[]
const CLARIFICATION_TYPES = Object.keys(ECO_CLARIFICATION_TYPE_LABELS) as EcoClarificationType[]

/** Черновик: варианты — текстом по одному в строке. */
interface Draft {
  input: EcoQuestionInput
  options: string
  clarOptions: string[]
}

const EMPTY: EcoQuestionInput = {
  section: 1,
  text: '',
  hint: '',
  answerType: 'yes_no',
  options: [],
  clarifications: [],
  showIfQuestionId: null,
  showIfAnswer: '',
  enabled: true,
}

function draftFrom(q: EcoQuestion | null): Draft {
  const input = q ? ecoQuestionToInput(q) : EMPTY
  return {
    input,
    options: input.options.join('\n'),
    clarOptions: input.clarifications.map((c) => c.options.join('\n')),
  }
}

function lines(text: string): string[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
}

interface Errors {
  text?: string
  options?: string
  clarifications?: string
  showIf?: string
}

function validate(d: Draft): Errors {
  const errors: Errors = {}
  if (!d.input.text.trim()) errors.text = 'Введите текст вопроса'
  const hasOptions = d.input.answerType === 'single' || d.input.answerType === 'multi'
  if (hasOptions) {
    const opts = lines(d.options)
    if (opts.length < 2 || new Set(opts).size !== opts.length) {
      errors.options = 'Укажите не меньше двух разных вариантов'
    }
  }
  if (d.input.answerType === 'yes_no') {
    d.input.clarifications.forEach((c, i) => {
      if (!c.label.trim()) errors.clarifications = 'Укажите подпись каждого уточнения'
      else if (c.type === 'single') {
        const opts = lines(d.clarOptions[i] ?? '')
        if (opts.length < 2 || new Set(opts).size !== opts.length) {
          errors.clarifications = `У уточнения «${c.label.trim()}» укажите не меньше двух разных вариантов`
        }
      }
    })
  }
  if (d.input.showIfQuestionId && !d.input.showIfAnswer) {
    errors.showIf = 'Выберите ответ, при котором показывать вопрос'
  }
  return errors
}

function toInput(d: Draft): EcoQuestionInput {
  const yesNo = d.input.answerType === 'yes_no'
  const hasOptions = d.input.answerType === 'single' || d.input.answerType === 'multi'
  return {
    ...d.input,
    text: d.input.text.trim(),
    hint: d.input.hint.trim(),
    options: hasOptions ? lines(d.options) : [],
    clarifications: yesNo
      ? d.input.clarifications.map((c, i) => ({
          ...c,
          label: c.label.trim(),
          options: c.type === 'single' ? lines(d.clarOptions[i] ?? '') : [],
        }))
      : [],
    showIfAnswer: d.input.showIfQuestionId ? d.input.showIfAnswer : '',
  }
}

export function QuestionDialog({
  open,
  question,
  questions,
  onClose,
  onSaved,
}: {
  open: boolean
  /** Правка вопроса; null — новый. */
  question: EcoQuestion | null
  /** Все вопросы — для «показывать, если». */
  questions: EcoQuestion[]
  onClose: () => void
  onSaved: (question: EcoQuestion) => void
}) {
  const formId = useId()
  const [draft, setDraft] = useState<Draft>(() => draftFrom(question))
  const [showErrors, setShowErrors] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setDraft(draftFrom(question))
    setShowErrors(false)
    setError(null)
  }, [open, question])

  const locked = Boolean(question?.builtin)
  const { input } = draft
  const errors = showErrors ? validate(draft) : {}
  const title = question ? 'Изменить вопрос' : 'Новый вопрос'
  const patch = (next: Partial<EcoQuestionInput>) =>
    setDraft((d) => ({ ...d, input: { ...d.input, ...next } }))

  // «Показывать, если» — по ответу «Да / Нет» или выбору варианта другого вопроса.
  const parents = useMemo(
    () =>
      questions.filter(
        (q) => q.id !== question?.id && (q.answerType === 'yes_no' || q.answerType === 'single'),
      ),
    [questions, question],
  )
  const parent = parents.find((q) => q.id === input.showIfQuestionId)

  function setClarification(i: number, next: Partial<EcoClarification>) {
    setDraft((d) => ({
      ...d,
      input: {
        ...d.input,
        clarifications: d.input.clarifications.map((c, j) => (j === i ? { ...c, ...next } : c)),
      },
    }))
  }

  function addClarification() {
    setDraft((d) => ({
      ...d,
      input: {
        ...d.input,
        clarifications: [
          ...d.input.clarifications,
          { key: '', type: 'date', label: '', options: [] },
        ],
      },
      clarOptions: [...d.clarOptions, ''],
    }))
  }

  function removeClarification(i: number) {
    setDraft((d) => ({
      ...d,
      input: { ...d.input, clarifications: d.input.clarifications.filter((_, j) => j !== i) },
      clarOptions: d.clarOptions.filter((_, j) => j !== i),
    }))
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (saving) return
    if (Object.keys(validate(draft)).length > 0) {
      setShowErrors(true)
      return
    }
    setError(null)
    setSaving(true)
    try {
      const body = toInput(draft)
      const saved = question
        ? await adminApi.ecology.updateQuestion(question.id, body)
        : await adminApi.ecology.createQuestion(body)
      onSaved(saved)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить вопрос'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !saving) onClose()
      }}
    >
      <DialogContent title={title} className="max-w-2xl">
        <Card
          scrollable
          className="max-h-dialog"
          header={title}
          footer={
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="secondary" onClick={onClose} disabled={saving}>
                Отмена
              </Button>
              <Button type="submit" form={formId} disabled={saving}>
                {saving ? 'Сохранение…' : 'Сохранить'}
              </Button>
            </div>
          }
        >
          <form id={formId} onSubmit={submit} noValidate>
            {locked ? (
              <Alert variant="info" className="mb-3">
                Вопрос из анкеты: на тип ответа, варианты и уточнения опираются правила, поэтому их
                менять нельзя. Можно поправить текст, подсказку, раздел, подписи уточнений или
                выключить вопрос.
              </Alert>
            ) : null}
            {error ? (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            ) : null}

            <Field label="Текст вопроса" required error={errors.text}>
              {(props) => (
                <textarea
                  {...props}
                  rows={2}
                  className={TEXTAREA_CLASS}
                  value={input.text}
                  onChange={(e) => patch({ text: e.target.value })}
                />
              )}
            </Field>

            <div className="grid grid-cols-1 gap-x-4 nw:grid-cols-2">
              <Field label="Раздел">
                {(props) => (
                  <Select
                    value={String(input.section)}
                    onValueChange={(v) => patch({ section: Number(v) })}
                  >
                    <SelectTrigger id={props.id} aria-label="Раздел" className="text-left">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ECO_SURVEY_SECTIONS.map((name, i) => (
                        <SelectItem key={name} value={String(i + 1)}>
                          {name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Тип ответа">
                {(props) => (
                  <Select
                    value={input.answerType}
                    disabled={locked}
                    onValueChange={(v) => patch({ answerType: v as EcoAnswerType })}
                  >
                    <SelectTrigger id={props.id} aria-label="Тип ответа" className="text-left">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ANSWER_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {ECO_ANSWER_TYPE_LABELS[t]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </div>

            {input.answerType === 'single' || input.answerType === 'multi' ? (
              <Field
                label="Варианты ответа"
                hint="По одному в строке"
                required
                error={errors.options}
              >
                {(props) => (
                  <textarea
                    {...props}
                    rows={4}
                    disabled={locked}
                    className={TEXTAREA_CLASS}
                    value={draft.options}
                    onChange={(e) => setDraft((d) => ({ ...d, options: e.target.value }))}
                  />
                )}
              </Field>
            ) : null}

            <Field label="Подсказка под вопросом">
              {(props) => (
                <Input
                  {...props}
                  value={input.hint}
                  placeholder="Например: отметьте всё, что есть"
                  onChange={(e) => patch({ hint: e.target.value })}
                />
              )}
            </Field>

            {input.answerType === 'yes_no' ? (
              <div className="my-4">
                <p className="mb-1.5 text-13 text-txt-secondary">Уточнения при ответе «Да»</p>
                {input.clarifications.length === 0 ? (
                  <p className="text-13 text-txt-tertiary">Нет уточнений</p>
                ) : null}
                <div className="flex flex-col gap-3">
                  {input.clarifications.map((c, i) => (
                    <div key={c.key || `new-${i}`} className="rounded-8 border border-line p-3">
                      <div className="flex flex-col gap-x-3 nw:flex-row nw:items-end">
                        <Field label="Тип" className="my-0 nw:w-48 nw:shrink-0">
                          {(props) => (
                            <Select
                              value={c.type}
                              disabled={locked}
                              onValueChange={(v) =>
                                setClarification(i, { type: v as EcoClarificationType })
                              }
                            >
                              <SelectTrigger
                                id={props.id}
                                aria-label={`Тип уточнения ${i + 1}`}
                                className="text-left"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {CLARIFICATION_TYPES.map((t) => (
                                  <SelectItem key={t} value={t}>
                                    {ECO_CLARIFICATION_TYPE_LABELS[t]}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </Field>
                        <Field label="Подпись" className="my-0 min-w-0 flex-1">
                          {(props) => (
                            <Input
                              {...props}
                              value={c.label}
                              aria-label={`Подпись уточнения ${i + 1}`}
                              placeholder="Например: Дата разработки"
                              onChange={(e) => setClarification(i, { label: e.target.value })}
                            />
                          )}
                        </Field>
                        {locked ? null : (
                          <Button
                            size="sm"
                            variant="secondary"
                            className="mt-2 self-start nw:mb-1 nw:mt-0 nw:self-auto"
                            title="Убрать уточнение"
                            aria-label={`Убрать уточнение ${i + 1}`}
                            onClick={() => removeClarification(i)}
                          >
                            <Trash2 className="size-4 stroke-current" aria-hidden />
                          </Button>
                        )}
                      </div>
                      {c.type === 'single' ? (
                        <Field label="Варианты" hint="По одному в строке" className="mb-0 mt-3">
                          {(props) => (
                            <textarea
                              {...props}
                              rows={3}
                              disabled={locked}
                              className={TEXTAREA_CLASS}
                              value={draft.clarOptions[i] ?? ''}
                              onChange={(e) =>
                                setDraft((d) => ({
                                  ...d,
                                  clarOptions: d.clarOptions.map((o, j) =>
                                    j === i ? e.target.value : o,
                                  ),
                                }))
                              }
                            />
                          )}
                        </Field>
                      ) : null}
                    </div>
                  ))}
                </div>
                {errors.clarifications ? (
                  <p role="alert" className="mt-1.5 text-12 text-red">
                    {errors.clarifications}
                  </p>
                ) : null}
                {!locked && input.clarifications.length < MAX_CLARIFICATIONS ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="mt-3"
                    leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
                    onClick={addClarification}
                  >
                    Добавить уточнение
                  </Button>
                ) : null}
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-x-4 nw:grid-cols-2">
              <Field label="Показывать вопрос" error={errors.showIf}>
                {(props) => (
                  <Select
                    value={input.showIfQuestionId ?? ALWAYS}
                    disabled={locked}
                    onValueChange={(v) =>
                      patch({ showIfQuestionId: v === ALWAYS ? null : v, showIfAnswer: '' })
                    }
                  >
                    <SelectTrigger
                      id={props.id}
                      aria-label="Показывать вопрос"
                      className="text-left"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALWAYS}>всегда</SelectItem>
                      {parents.map((q) => (
                        <SelectItem key={q.id} value={q.id}>
                          если «{q.text}»
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              {input.showIfQuestionId ? (
                <Field label="Ответ">
                  {(props) => (
                    <Select
                      value={input.showIfAnswer || undefined}
                      disabled={locked}
                      onValueChange={(v) => patch({ showIfAnswer: v })}
                    >
                      <SelectTrigger
                        id={props.id}
                        aria-label="Ответ для показа"
                        className="text-left"
                      >
                        <SelectValue placeholder="Выберите ответ" />
                      </SelectTrigger>
                      <SelectContent>
                        {(parent ? ecoAnswerValues(parent) : []).map((v) => (
                          <SelectItem key={v} value={v}>
                            {v}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Field>
              ) : null}
            </div>

            <LabeledCheckbox
              className="my-4"
              checked={input.enabled}
              onCheckedChange={(enabled) => patch({ enabled })}
            >
              Задавать вопрос в опросе
            </LabeledCheckbox>
          </form>
        </Card>
      </DialogContent>
    </Dialog>
  )
}
